import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { Map, List } from 'immutable';

import { AppPage } from '../AppPage';
import { ResizableSection } from '../helpers/ResizableSection';
import Button from '../helpers/Button';
import { ProjectDetails } from './ProjectDetails';
import { MidPanel } from './MidPanel';
import { TargetTable } from './TargetTable';

import style from './style.module.css';

const PROJECT_BACKGROUND = '#d7c4c4';

/**
 * Project component.
 *
 * @param props {Object}.
 * @param props.projectData {Immutable.Map} Project data.
 * @param props.users {Immutable.List} User list.
 * @param props.approvals {Immutable.List} Approval list.
 * @param props.templates {Immutable.List} Template list.
 * @param props.targets {Immutable.List} Target list.
 * @param props.common {Immutable.Map} Common object.
 * @param props.targetColumns {Immutable.List} Target column configurations.
 * @param props.approvalColumns {Immutable.List} Approval column configurations.
 * @param props.savePending {Boolean} Allow to click saving button if true.
 * @param props.onRowClick {Function} Handle event of clicking on a row.
 * @param props.onRowDoubleClick {Function} Handle event of double clicking on a row.
 * @param props.onRowInsert {Function} Handle event of adding a new row.
 * @param props.onDeleteApprovalRow {Function} Handle event of deleting an approval list.
 * @param props.onTextChange {Function} Handle event of changing text.
 * @param props.onReset {Function} Handle event of reset all tables to view mode.
 * @param props.onSort {Function} Handle event of sorting table.
 * @param props.onReload {Function} Handle event of reloading page.
 * @param props.onSave {Function} Handle event of saving page.
 * @param props.onExport {Function} Handle event of exporting project information.
 * @param props.onDownload {Function} Handle event of downloading template file.
 * @param props.onEdit {Function} Handle event of opening the project details popup.
 * @param props.savedDetails {Object} Saved details shown in the widget while the edit popup is open.
 */
const ProjectComponent = ({
  approvalColumns,
  projectData,
  users,
  approvals,
  noApprovals,
  templates,
  targets,
  common,
  targetColumns,
  savePending,
  savedDetails,
  onRowClick,
  onRowDoubleClick,
  onRowInsert,
  onDeleteApprovalRow,
  onTextChange,
  onReset,
  onSort,
  onReload,
  onSave,
  onExport,
  onDownload,
  onEdit,
  onChange,
  onSubmit,
  onTableRef,
  onNoApprovalDoubleClick,
  onCeoProjectTargetChange,
}) => {
  const details = savedDetails || { projectData, users, templates, common };
  const industryNames = details.common
    .get('bic')
    .concat(details.common.get('bm'))
    .map(industry => industry.get('name'))
    .join(', ');
  const userNames = details.users
    .filter(user => user.get('id') > 0)
    .map(user => user.get('name'))
    .join(', ');
  const toolbar = (
    <div className={style.toolbar}>
      <Button className="btn-primary" disabled={!savePending} onClick={onSave}>
        <i aria-hidden="true" className="fa fa-check" /> Save
      </Button>
      <Button onClick={onReload}>
        <i aria-hidden="true" className="fa fa-refresh" /> Reload
      </Button>
      <Button onClick={onExport}>
        <i aria-hidden="true" className="fa fa-download" /> Export Universe
      </Button>
    </div>
  );

  return (
    <div className={style.host} onClick={onReset}>
      <AppPage
        background={PROJECT_BACKGROUND}
        overline="Project"
        title={details.projectData.getIn(['category', 'name']) || 'Project'}
        toolbar={toolbar}
      >
        <ProjectDetails
          industryNames={industryNames}
          isLoading={details.common.get('projectLoading')}
          onDownload={onDownload}
          onEdit={onEdit}
          projectData={details.projectData}
          templates={details.templates}
          userNames={userNames}
        />
        <ResizableSection className={style.approvals} defaultHeight={208} storageKey="projectApprovalsHeight">
          <MidPanel
            approvalColumns={approvalColumns}
            approvals={approvals}
            common={common}
            noApprovals={noApprovals}
            onDeleteApprovalRow={onDeleteApprovalRow}
            onNoApprovalDoubleClick={onNoApprovalDoubleClick}
            onRowChange={onTextChange}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            onRowInsert={onRowInsert}
            onSort={onSort}
          />
        </ResizableSection>
        <TargetTable
          common={common}
          onCeoProjectTargetChange={onCeoProjectTargetChange}
          onChange={onChange}
          onDoubleClick={onRowDoubleClick}
          onSort={onSort}
          onSubmit={onSubmit}
          onTableRef={onTableRef}
          projectData={details.projectData}
          targetColumns={targetColumns}
          targets={targets}
        />
      </AppPage>
    </div>
  );
};

ProjectComponent.propTypes = {
  approvalColumns: PropTypes.instanceOf(List).isRequired,
  approvals: PropTypes.instanceOf(List).isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  noApprovals: PropTypes.instanceOf(List).isRequired,
  onDeleteApprovalRow: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onReload: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onRowInsert: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onTextChange: PropTypes.func,
  projectData: PropTypes.instanceOf(Map).isRequired,
  savedDetails: PropTypes.shape({
    common: PropTypes.instanceOf(Map).isRequired,
    projectData: PropTypes.instanceOf(Map).isRequired,
    templates: PropTypes.instanceOf(List).isRequired,
    users: PropTypes.instanceOf(List).isRequired,
  }),
  savePending: PropTypes.bool.isRequired,
  targetColumns: PropTypes.instanceOf(List).isRequired,
  targets: PropTypes.instanceOf(List).isRequired,
  templates: PropTypes.instanceOf(List).isRequired,
  users: PropTypes.instanceOf(List).isRequired,
};

export const Project = memo(ProjectComponent);
